import { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useGetAdminNavMenusQuery } from '../../services/menuApi'

interface MenuAccessGuardProps {
  children: ReactNode
}

/**
 * 현재 경로에 대한 메뉴 R(읽기) 권한을 체크하고 없으면 /forbidden 으로 리다이렉트한다.
 *
 * ## 매칭 규칙
 * 1. 아래 `PUBLIC_PREFIXES` 에 등록된 공용 경로(대시보드/마이페이지/개발도구/에러 페이지 등)는
 *    메뉴 마스터와 무관하므로 항상 통과.
 * 2. 그 외 경로는 `useGetAdminNavMenusQuery` 가 반환한 "본인 권한으로 볼 수 있는 메뉴" 트리에서
 *    현재 경로와 prefix 매칭되는 메뉴를 찾는다.
 *    - 백엔드(selectAdminNavMenuList)가 이미 R 권한 있는 메뉴만 내려주므로, 응답에 포함되지 않은 경로는
 *      곧 "본인이 볼 수 없는 메뉴" 로 간주해 /forbidden 으로 보낸다.
 *    - 매칭된 메뉴가 있어도 `auth_r !== 'Y'` 면 동일하게 /forbidden.
 * 3. 메뉴 데이터 로딩 중이면 children 을 렌더하지 않고 빈 화면을 반환 (AdminLayout 이 이미 스피너 표시).
 *
 * 프론트 `useMenuPermission` 훅의 매칭 규칙과 의미적으로 대칭이다 (가장 긴 prefix 우선).
 */

// 메뉴 마스터와 무관한 공용 경로 — 항상 통과.
// 새 화이트리스트 경로를 추가할 때는 반드시 이 배열에 등록한다.
const PUBLIC_PREFIXES = [
  '/dashboard',
  '/mypage',
  '/profile',
  '/test',
  '/dev',
  '/forbidden',
  '/error',
  '/sitemap',
  '/ui-showcase',
  '/force-password-change',
]

const normalize = (p: string): string => {
  const trimmed = p.replace(/\/+$/, '')
  return trimmed || '/'
}

export default function MenuAccessGuard({ children }: MenuAccessGuardProps) {
  const location = useLocation()
  const { data, isLoading, isUninitialized } = useGetAdminNavMenusQuery()

  // 메뉴 로딩 완료 전에는 guard 판단을 미룬다. AdminLayout 이 자체 스피너를 표시하고 있어도
  // 이 단계에서 children 을 렌더하면 각 페이지가 권한 응답 없이 먼저 마운트돼 다른 API 를 때릴 수 있음.
  if (isLoading || isUninitialized) {
    return null
  }

  const target = normalize(location.pathname)

  // 1) 공용 화이트리스트 통과
  if (PUBLIC_PREFIXES.some((p) => target === p || target.startsWith(p + '/'))) {
    return <>{children}</>
  }

  // 2) 본인 권한 메뉴에서 가장 긴 prefix 매칭 탐색
  const menus = data?.data ?? []
  const candidates = menus
    .filter((m) => m.lnkg_addr && m.lnkg_addr.length > 0)
    .map((m) => ({ menu: m, addr: normalize(m.lnkg_addr as string) }))
    .sort((a, b) => b.addr.length - a.addr.length)

  const matched = candidates.find(
    ({ addr }) => target === addr || target.startsWith(addr + '/'),
  )?.menu

  if (matched && matched.auth_r === 'Y') {
    return <>{children}</>
  }

  // 3) 매칭되는 메뉴가 없거나 R 권한이 없으면 403
  return <Navigate to="/forbidden" replace />
}
